import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { BatchCategory } from '@content-platform/shared';
import { ApiError, api, apiErrorMessage } from '../lib/api';
import Pagination from '../components/Pagination';
import { useUiStore } from '../store';

const PAGE_SIZE = 20;

type BatchNotice = { kind: 'empty' } | { kind: 'multi'; count: number };

export default function Dashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setUsageAlert = useUiStore((s) => s.setUsageAlert);
  const [batchNotice, setBatchNotice] = useState<BatchNotice | null>(null);

  const drafts = useQuery({ queryKey: ['drafts'], queryFn: () => api.listDrafts() });

  const [page, setPage] = useState(1);
  const totalPages = drafts.data
    ? Math.max(1, Math.ceil(drafts.data.drafts.length / PAGE_SIZE))
    : 1;
  const currentPage = page > totalPages ? 1 : page;
  const pagedDrafts = useMemo(() => {
    if (!drafts.data) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return drafts.data.drafts.slice(start, start + PAGE_SIZE);
  }, [drafts.data, currentPage]);

  const batchMutation = useMutation({
    mutationFn: (category?: BatchCategory) => api.runBatch(category ? { category } : {}),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      setUsageAlert(res.usageAlert ?? null);
      // 5.13: 0건 (LLM 판단) / 1건 (기존 navigate) / N건 (목록 갱신 + 안내)
      const n = res.draftIds.length;
      if (n === 0) {
        setBatchNotice({ kind: 'empty' });
      } else if (n === 1) {
        const only = res.draftIds[0];
        setBatchNotice(null);
        if (only) navigate(`/editor/${only}`);
      } else {
        setBatchNotice({ kind: 'multi', count: n });
      }
    },
    onError: (err) => {
      toast.error('배치 실패', { description: apiErrorMessage(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (draftId: string) => api.deleteDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('삭제 완료');
    },
    onError: (err) => {
      toast.error('삭제 실패', { description: apiErrorMessage(err) });
    },
  });

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Drafts</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => batchMutation.mutate('news')}
            disabled={batchMutation.isPending}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {batchMutation.isPending && batchMutation.variables === 'news' ? '실행 중…' : '뉴스'}
          </button>
          <button
            type="button"
            onClick={() => batchMutation.mutate('community')}
            disabled={batchMutation.isPending}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {batchMutation.isPending && batchMutation.variables === 'community' ? '실행 중…' : '커뮤니티'}
          </button>
        </div>
      </header>

      {batchNotice?.kind === 'empty' && (
        <div className="mb-3 flex items-center gap-3 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <span>
            LLM이 흥미로운 입력 없음으로 판단 → draft 0건. RSS 갱신을 기다린 후 다시 시도.
          </span>
          <button
            type="button"
            onClick={() => setBatchNotice(null)}
            className="ml-auto rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-100"
          >
            닫기
          </button>
        </div>
      )}

      {batchNotice?.kind === 'multi' && (
        <div className="mb-3 flex items-center gap-3 rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span>
            <strong>draft {batchNotice.count}건 생성</strong> — 아래 목록에서 검토 후 [발행].
          </span>
          <button
            type="button"
            onClick={() => setBatchNotice(null)}
            className="ml-auto rounded border border-blue-300 px-2 py-0.5 text-xs hover:bg-blue-100"
          >
            닫기
          </button>
        </div>
      )}

      {drafts.isLoading && <p className="text-slate-500">불러오는 중…</p>}
      {drafts.error && (
        <p className="text-red-600">
          오류: {drafts.error instanceof ApiError ? drafts.error.message : String(drafts.error)}
        </p>
      )}
      {drafts.data && drafts.data.drafts.length === 0 && (
        <p className="text-slate-500">draft가 없습니다. [배치] 실행으로 시작하세요.</p>
      )}

      <ul className="space-y-2">
        {pagedDrafts.map((d) => {
          const title = d.frontmatter.title ?? '(제목 미정)';
          const isDeleting =
            deleteMutation.isPending && deleteMutation.variables === d.id;
          return (
            <li
              key={d.id}
              className="flex items-start justify-between gap-3 rounded border bg-white p-3 shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <Link to={`/editor/${d.id}`} className="font-medium hover:underline">
                  {title}
                </Link>
                <div className="mt-1 text-xs text-slate-500">
                  {d.status === 'published' ? (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-800">
                      발행됨
                    </span>
                  ) : (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{d.status}</span>
                  )}
                  <span className="ml-2">
                    수정 {new Date(d.updatedAt).toLocaleString('ko-KR')}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (confirm(`"${title}" draft를 삭제할까요? 되돌릴 수 없음.`)) {
                    deleteMutation.mutate(d.id);
                  }
                }}
                disabled={isDeleting}
                className="shrink-0 rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {isDeleting ? '삭제 중…' : '삭제'}
              </button>
            </li>
          );
        })}
      </ul>

      <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
